/*
 * File: GetStudent.ts
 * Author: Austin Robinson
 * Date Created: 7/22/2023
 * Copyright (c) DrivingDoc 2023
 */
import { createQuery } from 'react-query-kit';

import { privateApi } from 'api';

export type GetStudentParams = {
  id: string;
};

export type GetStudentResponse = {
  tenant_id: string;
  id: string;
  title: string | null;
  first_name: string | null;
  middle_name: string | null;
  last_name: string | null;
  nickname: string | null;
  email: string | null;
  date_of_birth: string | null;
  sex: string | null;
  active: boolean;
  created_at: string | null;
  updated_at: string | null;
  created_by: string | null;
  updated_by: string | null;
  onboarding_sent_at: string | null;
  activated_at: string | null;
  needs_onboarding?: boolean;
  email_verification_request: {
    contact_type: 'email';
    contact_value: string;
    expires_at: string;
    status: 'pending' | 'verified';
    sent_at: string;
    verified_at: string;
  } | null;
  student: {
    tenant_id: string;
    user_id: string;
    permit_num: string | null;
    permit_issued: string | null;
    permit_expiration: string | null;
    permit_restrictions: string | null;
    permit_endorsements: string | null;
    permit_created_at: string | null;
    permit_updated_at: string | null;
    medical_conditions: string | null;
    high_school: string | null;
    parents: {
      tenant_id: string;
      id: string;
      student_user_id: string;
      first_name: string;
      last_name: string;
      relation: string | null;
      email: string | null;
      cell_phone: string | null;
      home_phone: string | null;
      work_phone: string | null;
      work_extension: string | null;
      email_verification_request: {
        contact_type: 'email';
        contact_value: string;
        expires_at: string;
        status: 'pending' | 'verified';
        sent_at: string;
        verified_at: string;
      } | null;
    }[];
  };
  user_addresses: {
    tenant_id: string;
    user_id: string;
    priority: number;
    name: string | null;
    street_address: string | null;
    apt_no: string | null;
    po_box_no: string | null;
    city: string | null;
    state: string | null;
    zip: string | null;
    country: string | null;
  }[];
  user_phones: {
    tenant_id: string;
    user_id: string;
    priority: number;
    name: string | null;
    type: string | null;
    phone: string | null;
    extension: string | null;
    phone_verification_request: {
      contact_type: 'sms';
      contact_value: string;
      expires_at: string;
      status: 'pending' | 'verified';
      sent_at: string;
      verified_at: string;
    } | null;
  }[];
};

async function getStudent(params: GetStudentParams) {
  if (!params.id) {
    return null;
  }
  try {
    // make api call
    const result = await privateApi.get(`${import.meta.env.VITE_API_BASE_URL}/users/students/${params.id}`, {});

    // return the data object
    return result.data as GetStudentResponse;
  } catch (e) {
    return null;
  }
}

const useGetStudent = createQuery<GetStudentResponse | null, GetStudentParams, Error>({
  queryKey: ['student_profile'],
  fetcher: (variables) => getStudent(variables),
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  // enabled: (data) => !data,
});

export { useGetStudent };
